import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import './SummaryAndPreview.scss';
import {useTranslation, Trans} from 'react-i18next';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {
  useGetSummaryView,
  useSaveSummary,
  useGetProductionPreview,
  useGetProductionPreviewFile,
} from '../../../../hooks/productions/useProductions';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {GetSummaryViewRequestParam} from '../../../../api/types/production.type';
import {
  ToastType,
  ModuleType,
  ProductionStatus,
} from '../CreateNewProductionUtils';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface SummaryAndPreviewProps {
  productionId: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

interface ProductionDocumentSummary {
  totalDocuments: number;
  totalPages: number;
  numberOfCustodians: number;
  numberOfNatives: number;
  numberOfMp3Files: number;
  exceptionDetails: number;
  documentsWithMissingTextFiles: number;
  firstDocId: string;
  lastDocId: string;
}

interface PrivilegedDocumentSummary {
  privilegedDocuments: number;
  documentsIdentifiedByProductionGuard: number;
  redactedDocuments: number;
  redactedNativeFileVariants: number;
  redactedExcelDocuments: number;
  redactedAudioDocuments: number;
  redactedTIFFPDFDocuments: number;
}

interface OcrTiffDocuments {
  documentsToOcr: number;
  pagesToOcr: number;
  documentsToImage: number;
  pagesToImage: number;
  documentsPrintedInColor: number;
  pagesPrintedInColor: number;
}

interface SummaryItemConfig {
  labelKey: string;
  tooltipContentKey: string;
  valueAccessor: (data: {
    productionSummary: ProductionDocumentSummary;
    privilegedSummary: PrivilegedDocumentSummary;
    ocrTiffSummary: OcrTiffDocuments;
  }) => string | number;
  noteKey?: string; // Optional note key (e.g., for "usesDocPagesMetadata")
  valueFormatter?: (value: string | number) => string; // Optional formatter function
}

const PREVIEW_DOWNLOAD_DELAY_MS = 3000;

const SummaryAndPreview = forwardRef<StepChildRef, SummaryAndPreviewProps>(
  (
    {
      productionId,
      isProductionSet,
      getStatus,
      goNext,
      onStateChange,
      productionStatusCode,
      isUpdateNeeded,
      onUpdateNeededChange,
    },
    ref
  ) => {
    const {t} = useTranslation();

    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;

    // Get securityGroupId from Redux
    const {securityGroupId} = useSelector(
      (state: RootState) => state.production
    );

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    // State for summary data
    const [productionSummary, setProductionSummary] =
      useState<ProductionDocumentSummary>({
        totalDocuments: 0,
        totalPages: 0,
        numberOfCustodians: 0,
        numberOfNatives: 0,
        numberOfMp3Files: 0,
        exceptionDetails: 0,
        documentsWithMissingTextFiles: 0,
        firstDocId: '',
        lastDocId: '',
      });

    const [privilegedSummary, setPrivilegedSummary] =
      useState<PrivilegedDocumentSummary>({
        privilegedDocuments: 0,
        documentsIdentifiedByProductionGuard: 0,
        redactedDocuments: 0,
        redactedNativeFileVariants: 0,
        redactedAudioDocuments: 0,
        redactedExcelDocuments: 0,
        redactedTIFFPDFDocuments: 0,
      });

    const [ocrTiffSummary, setOcrTiffSummary] = useState<OcrTiffDocuments>({
      documentsToOcr: 0,
      pagesToOcr: 0,
      documentsToImage: 0,
      pagesToImage: 0,
      documentsPrintedInColor: 0,
      pagesPrintedInColor: 0,
    });

    // State for save/next buttons
    const [isSaved, setIsSaved] = useState<boolean>(false);

    // API hooks
    const {data: summaryViewData} = useGetSummaryView(
      {
        SecurityGroupId: securityGroupId,
        moduletype: moduleType,
        productionId: productionId,
      } as GetSummaryViewRequestParam,
      !!securityGroupId && !!productionId
    );

    const {mutate: saveSummary, isPending: isSaving} = useSaveSummary();

    // State for preview
    const [previewEnabled, setPreviewEnabled] = useState<boolean>(false);
    const hasDownloadedRef = useRef<boolean>(false);
    const downloadPreviewFileTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const {refetch: refetchPreview} = useGetProductionPreview(
      {productionId: productionId},
      false
    );

    const {mutate: downloadPreviewFile, isPending: isDownloadingPreview} =
      useGetProductionPreviewFile(
        () => {
          getStatus(true, ToastType.Success, t('common.success'));
          hasDownloadedRef.current = false; // Reset after successful download
        },
        (error) => {
          getStatus(true, ToastType.Error, error?.message || t('common.error'));
          hasDownloadedRef.current = false; // Reset on error
        }
      );

    // Map API response to component state
    useEffect(() => {
      if (summaryViewData) {
        // Map Production Document Summary
        setProductionSummary({
          totalDocuments: summaryViewData.totalDocuments || 0,
          totalPages: summaryViewData.totalPages || 0,
          numberOfCustodians: summaryViewData.numberOfCustodians || 0,
          numberOfNatives: summaryViewData.numberOfNatives || 0,
          numberOfMp3Files: summaryViewData.mP3FileCount || 0,
          exceptionDetails: summaryViewData.numberOfExceptionFiles || 0,
          documentsWithMissingTextFiles: summaryViewData.missingTextFiles || 0,
          firstDocId: summaryViewData.firstDocID || '',
          lastDocId: summaryViewData.lastDocID || '',
        });

        // Map Privileged Document Summary
        setPrivilegedSummary({
          privilegedDocuments: summaryViewData.privilegedDocuments || 0,
          documentsIdentifiedByProductionGuard:
            summaryViewData.documentsIdentifiedByProductionGuard || 0,
          redactedDocuments: summaryViewData.redactedDocuments || 0,
          redactedNativeFileVariants:
            summaryViewData.redactedNativeFileVariants || 0,
          redactedAudioDocuments: summaryViewData.redactedAudioDocuments || 0,
          redactedExcelDocuments: summaryViewData.redactedExcelDocuments || 0,
          redactedTIFFPDFDocuments:
            summaryViewData.redactedTIFFPDFDocuments || 0,
        });

        // Map OCR/TIFF Documents
        setOcrTiffSummary({
          documentsToOcr: summaryViewData.documentsToOCR || 0,
          pagesToOcr: summaryViewData.pagesToOCR || 0,
          documentsToImage: summaryViewData.documentsToTiff || 0,
          pagesToImage: summaryViewData.pagesToTiff || 0,
          documentsPrintedInColor: summaryViewData.documentsPrintedInColor || 0,
          pagesPrintedInColor: summaryViewData.pagesPrintedInColor || 0,
        });
      }
    }, [summaryViewData]);

    // Handle preview button click
    const handlePreview = async () => {
      if (!productionId) {
        getStatus(true, ToastType.Error, t('common.missingRequiredFields'));
        return;
      }

      hasDownloadedRef.current = false;
      setPreviewEnabled(true);

      const {data: previewData, error: previewError} = await refetchPreview();

      if (previewError) {
        getStatus(
          true,
          ToastType.Error,
          previewError.message || t('common.error')
        );
        setPreviewEnabled(false);
        return;
      }

      if (!previewData) {
        setPreviewEnabled(false);
        return;
      }

      if (previewData.fileExists && previewData.fileName) {
        // Mark as downloaded to prevent duplicate calls
        hasDownloadedRef.current = true;
        // Download the preview file using the filename exactly as received, 3 seconds
        downloadPreviewFileTimeoutRef.current = setTimeout(() => {
          downloadPreviewFile({filePath: previewData.fileName});
          setPreviewEnabled(false);
        }, PREVIEW_DOWNLOAD_DELAY_MS);
      } else if (!previewData.fileExists) {
        getStatus(
          true,
          ToastType.Warning,
          t('common.fileNotFound') || 'Preview file not found'
        );
        setPreviewEnabled(false);
      }
    };

    // Clear any pending preview-download timer only when the component unmounts
    useEffect(() => {
      return () => {
        if (downloadPreviewFileTimeoutRef.current) {
          clearTimeout(downloadPreviewFileTimeoutRef.current);
        }
      };
    }, []);

    // Handle save
    const handleSave = () => {
      if (!securityGroupId || !productionId) {
        getStatus(true, ToastType.Error, t('common.missingRequiredFields'));
        return;
      }

      saveSummary(
        {
          productionId: productionId,
          securityGroupId: securityGroupId,
          isMarkingComplete: false,
        },
        {
          onSuccess: (data) => {
            setIsSaved(true);
            getStatus(true, ToastType.Success, data.message);
            // Update parent state to track that update is needed after save
            onUpdateNeededChange?.(true);
          },
          onError: (error) => {
            getStatus(
              true,
              ToastType.Error,
              error?.message || t('common.saveFailed')
            );
          },
        }
      );
    };

    // Handle next
    const handleNext = () => {
      if (isInProgressOrCompleted || !isUpdateNeeded) {
        goNext?.();
        return;
      }
      if (!securityGroupId || !productionId) {
        getStatus(true, ToastType.Error, t('common.missingRequiredFields'));
        return;
      }

      saveSummary(
        {
          productionId: productionId,
          securityGroupId: securityGroupId,
          isMarkingComplete: true,
        },
        {
          onSuccess: () => {
            setIsSaved(true);
            getStatus(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.common.nextSuccessToast'
              )
            );
            goNext?.();
          },
          onError: (error) => {
            getStatus(
              true,
              ToastType.Error,
              error?.message || t('common.saveFailed')
            );
          },
        }
      );
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: handleSave,
      next: handleNext,
    }));

    // Calculate button states
    const isSaveDisabled = isSaving || isSaved;
    const isNextDisabled = isSaving;

    // Report state changes to parent
    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    // Configuration arrays for summary items
    const productionDocumentSummaryItems: SummaryItemConfig[] = [
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.totalDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.totalDocumentsTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.totalDocuments,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.totalPages',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.totalPagesTooltip',
        valueAccessor: ({productionSummary}) => productionSummary.totalPages,
        noteKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.usesDocPagesMetadata',
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfCustodians',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfCustodiansTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.numberOfCustodians,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfNatives',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfNativesTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.numberOfNatives,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfMp3Files',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.numberOfMp3FilesTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.numberOfMp3Files,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.exceptionDetails',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.exceptionDetailsTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.exceptionDetails,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.documentsWithMissingTextFiles',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.documentsWithMissingTextFilesTooltip',
        valueAccessor: ({productionSummary}) =>
          productionSummary.documentsWithMissingTextFiles,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.firstAndLastDocIds',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.firstAndLastDocIdsTooltip',
        valueAccessor: ({productionSummary}) => {
          const first = productionSummary.firstDocId;
          const last = productionSummary.lastDocId;
          return `${first}and${last}`;
        },
      },
    ];

    const privilegedDocumentSummaryItems: SummaryItemConfig[] = [
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.privilegedDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.privilegedDocumentsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.privilegedDocuments,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.documentsIdentifiedByProductionGuard',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.documentsIdentifiedByProductionGuardTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.documentsIdentifiedByProductionGuard,
      },
      //remove it once rnfv feature live
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedDocumentsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.redactedDocuments,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedTIFFPDFDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedTIFFPDFDocumentsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.redactedTIFFPDFDocuments,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedExcelDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedExcelDocumentsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.redactedExcelDocuments,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedNativeFileVariants',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedNativeFileVariantsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.redactedNativeFileVariants,
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedAudioDocuments',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.redactedAudioDocumentsTooltip',
        valueAccessor: ({privilegedSummary}) =>
          privilegedSummary.redactedAudioDocuments,
      },
    ];

    const ocrTiffDocumentsItems: SummaryItemConfig[] = [
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsToOcr',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsToOcrTooltip',
        valueAccessor: ({ocrTiffSummary}) => {
          return `${ocrTiffSummary.documentsToOcr}Docsand${ocrTiffSummary.pagesToOcr}Pages`;
        },
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsToImage',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsToImageTooltip',
        valueAccessor: ({ocrTiffSummary}) => {
          return `${ocrTiffSummary.documentsToImage}Docsand${ocrTiffSummary.pagesToImage}Pages`;
        },
      },
      {
        labelKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsPrintedInColor',
        tooltipContentKey:
          'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.documentsPrintedInColorTooltip',
        valueAccessor: ({ocrTiffSummary}) => {
          return `${ocrTiffSummary.documentsPrintedInColor}Docsand${ocrTiffSummary.pagesPrintedInColor}Pages`;
        },
      },
    ];

    // Render summary item helper function
    const renderSummaryItem = (
      item: SummaryItemConfig,
      index: number,
      itemsLength: number
    ) => {
      const summaryData = {
        productionSummary,
        privilegedSummary,
        ocrTiffSummary,
      };
      const value = item.valueAccessor(summaryData);
      const formattedValue = item.valueFormatter?.(value) ?? String(value ?? 0);
      //remove it once rnfv feature live
      const isRedactedNativeFileVariants = item.labelKey.endsWith(
        'redactedNativeFileVariants'
      );

      return (
        <Box
          key={index}
          className={
            isRedactedNativeFileVariants
              ? 'summaryItem summaryItemHidden'
              : 'summaryItem'
          }
          style={index === itemsLength - 1 ? {paddingBottom: 0} : undefined}
        >
          {item.noteKey ? (
            <Box className="summaryLabelContainer">
              <Box className="summaryLabel">
                <Typography
                  component="div"
                  variant="body2"
                  className="typo-text body3_size summaryText"
                  display="flex"
                  alignItems="center"
                  data-testid={`summary-and-preview-label-${index}`}
                >
                  {t(item.labelKey)}
                  <ArrTooltipWrapper
                    closeTooltipOnEscPress
                    trigger={<InfoIcon tabIndex={0} className="info-icon" />}
                    placement="right"
                  >
                    <Trans
                      i18nKey={item.tooltipContentKey}
                      components={{
                        br: <br data-testid={`summary-and-preview-br`} />,
                        strong: (
                          <strong data-testid={`summary-and-preview-strong`} />
                        ),
                        p: <p data-testid={`summary-and-preview-p`} />,
                      }}
                    />
                  </ArrTooltipWrapper>
                </Typography>
              </Box>
              <Typography
                component="div"
                variant="body2"
                className="typo-text body3_size summaryNote"
                data-testid={`summary-and-preview-note-key-${index}`}
              >
                {t(item.noteKey)}
              </Typography>
            </Box>
          ) : (
            <Box className="summaryLabel">
              <Typography
                component="div"
                variant="body2"
                className="typo-text body3_size summaryText"
                display="flex"
                alignItems="center"
                data-testid={`summary-and-preview-label-key-${index}`}
              >
                {t(item.labelKey)}
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  trigger={<InfoIcon tabIndex={0} className="info-icon" />}
                  placement="right"
                >
                  <Trans
                    i18nKey={item.tooltipContentKey}
                    components={{
                      br: <br data-testid={`summary-and-preview-br-btn`} />,
                      strong: (
                        <strong
                          data-testid={`summary-and-preview-strong-btn`}
                        />
                      ),
                      p: <p data-testid={`summary-and-preview-p-text`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Typography>
            </Box>
          )}
          <Typography
            component="div"
            variant="body1"
            className="typo-text subhead5_size summaryValue"
            data-testid={`summary-and-preview-formatted-value`}
          >
            {formattedValue}
          </Typography>
        </Box>
      );
    };

    return (
      <Box className="summaryPreviewContainer">
        <Box className="summaryPreviewCard">
          {/* Header */}
          <Box className="cardHeader">
            <Typography
              className="typo-text subhead2_size "
              component="div"
              variant="h6"
              data-testid={`summary-and-preview-header-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.summaryAndPreview.headerText'
              )}
            </Typography>
          </Box>

          {/* Scrollable Content */}
          <Box className="cardContent">
            {/* Production Document Summary Section */}
            <Box className="section">
              <Box className="title-section">
                <Typography
                  component="div"
                  className="typo-text subhead4_size sectionHeading"
                  display="flex"
                  alignItems="center"
                  data-testid={`summary-and-preview-title`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.title'
                  )}
                  <ArrTooltipWrapper
                    closeTooltipOnEscPress
                    trigger={<InfoIcon tabIndex={0} className="info-icon" />}
                    placement="right"
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.summaryAndPreview.productionDocumentSummary.titleTooltip"
                      components={{
                        br: (
                          <br data-testid={`summary-and-preview-br-button`} />
                        ),
                        strong: (
                          <strong
                            data-testid={`summary-and-preview-strong-button`}
                          />
                        ),
                        p: <p data-testid={`summary-and-preview-p-btn`} />,
                      }}
                    />
                  </ArrTooltipWrapper>
                </Typography>
              </Box>

              <Box className="summaryItemsContainer">
                {productionDocumentSummaryItems.map((item, index) =>
                  renderSummaryItem(
                    item,
                    index,
                    productionDocumentSummaryItems.length
                  )
                )}
              </Box>
            </Box>

            {/* Privileged Document Summary Section */}
            <Box className="section">
              <Box className="title-section">
                <Typography
                  component="div"
                  variant="subtitle1"
                  className="typo-text subhead4_size sectionHeading"
                  display="flex"
                  alignItems="center"
                  data-testid={`summary-and-preview-title-text`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.title'
                  )}
                  <ArrTooltipWrapper
                    closeTooltipOnEscPress
                    trigger={<InfoIcon tabIndex={0} className="info-icon" />}
                    placement="right"
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.summaryAndPreview.privilegedDocumentSummary.titleTooltip"
                      components={{
                        br: <br data-testid={`summary-and-preview-br-icon`} />,
                        strong: (
                          <strong
                            data-testid={`summary-and-preview-strong-icon`}
                          />
                        ),
                        p: <p data-testid={`summary-and-preview-p-button`} />,
                      }}
                    />
                  </ArrTooltipWrapper>
                </Typography>
              </Box>

              <Box className="summaryItemsContainer">
                {privilegedDocumentSummaryItems
                  .filter(
                    (item) =>
                      !item.labelKey.endsWith('redactedTIFFPDFDocuments') &&
                      !item.labelKey.endsWith('redactedExcelDocuments') &&
                      !item.labelKey.endsWith('redactedNativeFileVariants') &&
                      !item.labelKey.endsWith('redactedAudioDocuments')
                  )
                  .map((item, index, visibleItems) =>
                    renderSummaryItem(item, index, visibleItems.length)
                  )}
                {/* {privilegedDocumentSummaryItems.map((item, index) =>
                  renderSummaryItem(
                    item,
                    index,
                    privilegedDocumentSummaryItems.length
                  )
                )} */}
              </Box>
            </Box>

            {/* OCR/TIFF Documents Section */}
            <Box className="section">
              <Box className="title-section">
                <Typography
                  component="div"
                  variant="subtitle1"
                  className="typo-text subhead4_size sectionHeading"
                  display="flex"
                  alignItems="center"
                  data-testid={`summary-and-preview-title-btn`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.title'
                  )}
                  <ArrTooltipWrapper
                    closeTooltipOnEscPress
                    trigger={<InfoIcon tabIndex={0} className="info-icon" />}
                    placement="right"
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.summaryAndPreview.ocrTiffDocuments.titleTooltip"
                      components={{
                        br: <br data-testid={`summary-and-preview-br-link`} />,
                        strong: (
                          <strong
                            data-testid={`summary-and-preview-strong-link`}
                          />
                        ),
                        p: <p data-testid={`summary-and-preview-p-icon`} />,
                      }}
                    />
                  </ArrTooltipWrapper>
                </Typography>
              </Box>

              <Box className="summaryItemsContainer">
                {ocrTiffDocumentsItems.map((item, index) =>
                  renderSummaryItem(item, index, ocrTiffDocumentsItems.length)
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Preview Panel */}
        <Box className="previewPanel">
          <Box className="previewCard">
            {/* Header */}
            <Box className="cardHeader">
              <Typography
                className="typo-text subhead2_size"
                component="div"
                variant="h6"
                data-testid={`summary-and-preview-title-button`}
              >
                {t(
                  'productionsAndExports.createNewProduction.summaryAndPreview.preview.title'
                )}
              </Typography>
            </Box>

            {/* Content */}
            <Box className="previewSection">
              {summaryViewData?.hasTiffPDF ? (
                <>
                  <Typography
                    component="div"
                    variant="body2"
                    className="typo-text body3_size previewDescription"
                    data-testid={`summary-and-preview-description`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.summaryAndPreview.preview.description'
                    )}
                  </Typography>
                  <ArrButton
                    variant="secondary"
                    size="large"
                    className="save-button"
                    onClick={handlePreview}
                    isAriaDisabled={
                      isDownloadingPreview ||
                      previewEnabled ||
                      isInProgressOrCompleted
                    }
                    dataTestId="summary-and-preview-button"
                  >
                    {isDownloadingPreview || previewEnabled
                      ? t('common.downloading')
                      : t(
                          'productionsAndExports.createNewProduction.summaryAndPreview.preview.button'
                        )}
                  </ArrButton>
                </>
              ) : (
                <Typography
                  component="div"
                  variant="body2"
                  className="typo-text body3_size previewDescription"
                  data-testid={`summary-and-preview-no-preview-message`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.summaryAndPreview.preview.noPreviewMessage'
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

SummaryAndPreview.displayName = 'SummaryAndPreview';

export default SummaryAndPreview;
